import { FC, useEffect, useRef, useState } from 'react';

import { ITokenSale, useLazyGetDisputeFileQuery } from '@entities/trades/token-sell';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { Tooltip } from '@mui/material';
import ErrorMessage from '@shared/components/ErrorMessage';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import { useModal } from '@shared/components/Modal';

interface IProps {
  tokenSale: ITokenSale;
}

const DisputePreview: FC<{ id: string }> = ({ id }) => {
  const [getDisputeFile, { isLoading, isError, error, isSuccess }] = useLazyGetDisputeFileQuery();
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const blob = await getDisputeFile({ id }).unwrap();
        const nextUrl = URL.createObjectURL(blob);
        setFileType(blob.type || 'application/octet-stream');
        setFileUrl(nextUrl);
        urlRef.current = nextUrl;
      } catch {}
    };

    load();

    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [id, getDisputeFile]);

  return (
    <>
      <h2>Dispute file</h2>

      {isError && <ErrorMessage error={error} />}
      {isLoading && <LoadingSpinner />}

      {isSuccess && fileType?.startsWith('image/') && (
        <img src={fileUrl ?? ''} alt='Dispute file' />
      )}

      {isSuccess && fileType === 'application/pdf' && (
        <iframe src={fileUrl ?? ''} width='100%' height='800' title='Dispute file' />
      )}

      {isSuccess &&
        fileType &&
        !fileType.startsWith('image/') &&
        fileType !== 'application/pdf' && (
          <div>
            Unsupported file type: {fileType}
            <div>
              <a href={fileUrl ?? ''} target='_blank' rel='noreferrer'>
                Open file
              </a>
            </div>
          </div>
        )}
    </>
  );
};

export const DownloadTokenSale: FC<IProps> = ({ tokenSale }) => {
  const { openModal } = useModal();

  const handleDownload = () => {
    openModal(<DisputePreview id={tokenSale.hash} />, 'lg');
  };

  if (!tokenSale.dispute_file_url) return null;

  return (
    <Tooltip title='Download dispute file'>
      <ReceiptIcon sx={{ color: '#1976d2', cursor: 'pointer' }} onClick={handleDownload} />
    </Tooltip>
  );
};
